import { useMemo } from 'react';
import { useLocale } from 'use-intl';
import { useTranslations } from '@/i18n/translations';
import { Select, type SelectProps } from '@leanin/ui';
import { useCircleLanguages } from '../../lib/queries/groups';
import { languageLabel } from './card-facts-view';

type Props = Omit<SelectProps, 'options' | 'value' | 'onChange' | 'placeholder' | 'defaultValue'> & {
  readonly value: string | null;
  readonly onChange: (next: string | null) => void;
  /** Offer a blank "Not specified" option that maps to `null`. */
  readonly allowEmpty?: boolean;
};

// The language a Circle meets in, picked from the directory's language facet.
// The current value is always an option even when the facet omits it, so a
// Circle saved with a tag no other directory Circle uses keeps it selectable.
export function LanguageSelect({ value, onChange, allowEmpty = false, ...rest }: Props) {
  const tc = useTranslations('Circles');
  const locale = useLocale();
  const facet = useCircleLanguages();

  const options = useMemo(() => {
    const tags = new Set(facet.data ?? []);
    if (value !== null) tags.add(value);
    const labelled = [...tags]
      .map((tag) => ({ value: tag, label: languageLabel(tag, locale) }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
    return allowEmpty || value === null
      ? [{ value: '', label: tc('languageSelect.notSpecified') }, ...labelled]
      : labelled;
  }, [facet.data, value, allowEmpty, locale, tc]);

  return (
    <Select
      {...rest}
      value={value ?? ''}
      options={options}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
    />
  );
}
